import React, { useState, useMemo } from 'react'
import { Modal, ContextNotification } from '@pandiafi/components'
import { Button } from '@pandiafi/design-system'
import { useTranslation } from 'react-i18next'
import { DateTime } from 'luxon'

import type { CoreDayReservableArea } from '../../../common/validations/contractDayReservableAreasSchema'
import { findActiveReservation, findFutureReservation } from './uiSelectors'

import { useAPITerminateContractDayReservableAreaReservation } from 'api/apiHooks/useAPITerminateContractDayReservableAreaReservation'
import { useAPICancelContractDayReservableAreaReservation } from 'api/apiHooks/useAPICancelContractDayReservableAreaReservation'
import { useCurrentContract } from 'hooks/useCurrentContract/useCurrentContract'
import { renderDate } from 'utils'

export const DayReservableAreaTerminateForm: React.FC<{
  area: CoreDayReservableArea
  onSuccess?: (effectiveEndISO: string) => void
  onClose: () => void
}> = ({ area, onSuccess, onClose }) => {
  const { t } = useTranslation(['storage', 'common'])
  const { currentContractId } = useCurrentContract()

  const endOfMonth = DateTime.now().endOf('month').toISODate()!
  const [error, setError] = useState<string | null>(null)
  const [terminatedISO, setTerminatedISO] = useState<string | null>(null)
  const [cancelled, setCancelled] = useState<boolean>(false)

  const { mutate: terminate, isPending: isTerminating } =
    useAPITerminateContractDayReservableAreaReservation(currentContractId)

  const { mutate: cancel, isPending: isCancelling } =
    useAPICancelContractDayReservableAreaReservation(currentContractId)

  const { currentReservation, isFutureReservation } = useMemo(() => {
    const active = findActiveReservation(area)
    const future = findFutureReservation(area)
    return {
      currentReservation: active ?? future ?? null,
      isFutureReservation: !active && !!future,
    }
  }, [area])

  const submit = () => {
    if (!currentContractId) {
      setError(t('storage:error.contractMissing'))
      return
    }
    setError(null)

    if (isFutureReservation) {
      cancel(
        { rentableHierarchyId: area.rentableHierarchyId },
        {
          onSuccess: () => {
            setCancelled(true)
            onSuccess?.(currentReservation?.startDate || '')
          },
          onError: (e) =>
            setError(
              (e as Error)?.message || t('storage:error.terminationFailed')
            ),
        }
      )
    } else {
      terminate(
        { endDate: endOfMonth, rentableHierarchyId: area.rentableHierarchyId },
        {
          onSuccess: () => {
            setTerminatedISO(endOfMonth)
            onSuccess?.(endOfMonth)
          },
          onError: (e) =>
            setError(
              (e as Error)?.message || t('storage:error.terminationFailed')
            ),
        }
      )
    }
  }

  const terminatedLabel = terminatedISO ? renderDate(terminatedISO) : null
  const isPending = isTerminating || isCancelling

  return (
    <>
      {error && <ContextNotification type="error">{error}</ContextNotification>}

      {terminatedISO ? (
        <Modal.Notifications>
          <ContextNotification type="success" aria-live="polite">
            {t('storage:terminate.success')}
            {terminatedLabel ? <> ({terminatedLabel})</> : null}
          </ContextNotification>
        </Modal.Notifications>
      ) : cancelled ? (
        <Modal.Notifications>
          <ContextNotification type="success" aria-live="polite">
            {t('storage:terminate.success')}
          </ContextNotification>
        </Modal.Notifications>
      ) : isFutureReservation ? (
        <Modal.Content>
          <div className="mb-6">
            {currentReservation?.startDate && (
              <>
                <p className="mb-6 text-sm">
                  {t('storage:terminate.futureReservationLabel')}
                </p>
                <p className="text-lg">
                  {renderDate(currentReservation.startDate)}
                </p>
              </>
            )}
          </div>
        </Modal.Content>
      ) : (
        <Modal.Content>
          <div className="mb-6">
            {currentReservation?.startDate && (
              <p className="mb-6 text-sm">
                {t('storage:terminate.reservationStartedOn', {
                  date: renderDate(currentReservation.startDate),
                })}
              </p>
            )}
            <p className="font-medium mb-2">
              {t('storage:terminate.endDateLabel')}
            </p>
            <p className="text-lg">{renderDate(endOfMonth)}</p>
          </div>
        </Modal.Content>
      )}

      <Modal.Buttons>
        {!terminatedISO && !cancelled ? (
          <>
            <Button variant="cancel" onClick={onClose}>
              {t('common:cancel')}
            </Button>
            <Button variant="secondary" onClick={submit} loading={isPending}>
              {t('storage:terminate.cta')}
            </Button>
          </>
        ) : (
          <Button variant="cancel" onClick={onClose}>
            {t('common:closeButton')}
          </Button>
        )}
      </Modal.Buttons>
    </>
  )
}
